import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useAddSubVarietyMutation } from 'common/api/endpoints/variety';
import { handleError } from 'common/api/utils/handleError';
import FormPrompt from 'common/components/FormPrompt';
import {
  WizardDropdown,
  WizardDropdownOption,
} from 'common/components/WizardControls/WizardDropdown';
import { Variety } from 'common/models';
import * as notifier from 'common/services/notification';
import { orange } from 'common/styles/colors';
import { FC, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { addSubVarietySchema } from 'utils/schemas/settings';
import {
  AddSettingBtn,
  AddSettingForm,
  dropdownHeightStyling,
} from '../../styles';
import { AddSubVarDto } from '../utils/functions';
import { CodeInput } from './styles';

type AddSubVariety = {
  code: string | null;
  regVariety: WizardDropdownOption<Variety> | null;
  orgVariety: WizardDropdownOption<Variety> | null;
};

export const AddSubVariety: FC<{
  regVarOptions: WizardDropdownOption<Variety>[];
  orgVarOptions: WizardDropdownOption<Variety>[];
  isLoading: boolean;
}> = ({ regVarOptions, orgVarOptions, isLoading }) => {
  const [showForm, setShowForm] = useState(false);

  const [addSubVariety, { isLoading: isSubmitting }] =
    useAddSubVarietyMutation();

  const methods = useForm<AddSubVariety>({
    defaultValues: {
      code: null,
      regVariety: null,
      orgVariety: null,
    },
    resolver: yupResolver(addSubVarietySchema),
  });
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = methods;

  const closeForm = () => {
    reset();
    setShowForm(false);
  };

  const onAddSubmit = async (data: AddSubVariety): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const { code, regVariety, orgVariety } = data;
    const dto = {
      code,
      regVarId: regVariety?.value?.id,
      orgVarId: orgVariety?.value?.id,
    } as AddSubVarDto;

    try {
      await addSubVariety(dto).unwrap();
      notifier.showSuccessMessage('New sub-variety added.');
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to add new sub-variety.');
    }
  };

  return showForm ? (
    <FormProvider<AddSubVariety> {...methods}>
      <AddSettingForm onSubmit={handleSubmit(onAddSubmit)}>
        <div>
          <CodeInput
            {...register('code')}
            placeholder='Code...'
            $isError={!!errors.code}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
          <Form.Check.Input hidden isInvalid={!!errors.code} />
          {errors.code && (
            <Form.Control.Feedback type='invalid'>
              {errors.code.message}
            </Form.Control.Feedback>
          )}
        </div>
        <WizardDropdown<Variety>
          name='regVariety'
          placeholder='Conventional...'
          formControl={control}
          options={regVarOptions}
          isLoading={isLoading}
          maxWidth='165px'
          styles={dropdownHeightStyling}
          isDisabled={isSubmitting}
        />
        <WizardDropdown<Variety>
          name='orgVariety'
          placeholder='Organic...'
          formControl={control}
          options={orgVarOptions}
          isLoading={isLoading}
          maxWidth='160px'
          styles={dropdownHeightStyling}
          isDisabled={isSubmitting}
        />
        <button type='submit' className='add-btn' disabled={isSubmitting}>
          Add Sub-Variety
        </button>
        <button
          type='button'
          className='cancel-btn'
          onClick={closeForm}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </AddSettingForm>
      <FormPrompt isDirty={isDirty} isSubmitting={isSubmitting} />
    </FormProvider>
  ) : (
    <AddSettingBtn type='button' onClick={() => setShowForm(true)}>
      <AddCircleOutlineOutlinedIcon htmlColor={orange} />
      Add New Sub-Variety
    </AddSettingBtn>
  );
};
